'use client';

import React from 'react';

import CopyToClipboard from 'react-copy-to-clipboard';
import { EmailShareButton, FacebookShareButton, TwitterShareButton } from 'react-share';

import { cn } from '~/lib/utils';

interface SocialShareProps {
  size?: 'small' | 'large';
  url?: string | null;
  className?: string;
}

export function SocialShare({ size = 'small', url, className }: SocialShareProps) {
  const [shareUrl, setShareUrl] = React.useState(url);
  const [copied, setCopied] = React.useState<boolean | string>('');

  React.useEffect(() => {
    if (!shareUrl) {
      setShareUrl(window.location.href);
    }
  }, [shareUrl]);

  if (!shareUrl) return null;

  const isLarge = size === 'large';
  const isSmall = size === 'small';
  return (
    <ul className={cn('flex justify-center gap-x-4', className)}>
      <li>
        <FacebookShareButton url={shareUrl}>
          <svg
            className={cn(isSmall && 'h-12 w-12', isLarge && 'lg:h-20 lg:w-20')}
            fill='none'
            height='64'
            viewBox='0 0 64 64'
            width='64'
            xmlns='http://www.w3.org/2000/svg'
          >
            <circle cx='32' cy='32' fill='#2546ED' r='32' />
            <path
              d='M28.377 47.4585V34.4971H23.9281V28.515H28.377V24.3375C28.377 19.7868 31.2579 17.5477 35.3174 17.5477C37.2619 17.5477 38.9331 17.6925 39.4201 17.7572V22.5129L36.6047 22.5142C34.3969 22.5142 33.8983 23.5633 33.8983 25.1028V28.515H39.8805L37.8864 34.4971H33.8983V47.4585H28.377Z'
              fill='white'
            />
          </svg>
        </FacebookShareButton>
      </li>
      <li>
        <TwitterShareButton url={shareUrl}>
          <svg
            className={cn(isSmall && 'h-12 w-12', isLarge && 'lg:h-20 lg:w-20')}
            fill='none'
            height='64'
            viewBox='0 0 64 64'
            width='64'
            xmlns='http://www.w3.org/2000/svg'
          >
            <circle cx='32' cy='32' fill='#25A0ED' r='32' />
            <path
              d='M47.9563 22.2337C46.7598 22.7322 45.5634 23.131 44.1676 23.2307C45.5634 22.4331 46.5604 21.137 47.059 19.6414C45.7628 20.4391 44.367 20.9376 42.8715 21.2367C41.675 19.9406 39.9801 19.1429 38.1854 19.1429C34.5962 19.1429 31.6051 22.0343 31.6051 25.7233C31.6051 26.2218 31.7048 26.7203 31.8045 27.2188C26.3209 26.9197 21.5352 24.3275 18.245 20.3394C17.6468 21.3364 17.3477 22.4331 17.3477 23.6295C17.3477 25.9227 18.5441 27.9167 20.239 29.1132C19.1423 29.1132 18.1453 28.8141 17.248 28.3156V28.4153C17.248 31.6057 19.5411 34.198 22.5322 34.7962C21.934 34.8959 21.4355 34.9956 20.8372 34.9956C20.4384 34.9956 20.0396 34.9956 19.6408 34.8959C20.4384 37.4882 22.931 39.3825 25.7227 39.4822C23.5292 41.2769 20.6378 42.2739 17.5471 42.2739C17.0485 42.2739 16.4503 42.2739 15.9518 42.1742C18.9429 43.9688 22.4325 45.0656 26.1215 45.0656C38.1854 45.0656 44.7658 35.0953 44.7658 26.4212C44.7658 26.1221 44.7658 25.823 44.7658 25.6236C45.9622 24.6266 47.059 23.5298 47.9563 22.2337Z'
              fill='white'
            />
          </svg>
        </TwitterShareButton>
      </li>
      <li>
        <EmailShareButton url={shareUrl}>
          <svg
            className={cn(isSmall && 'h-12 w-12', isLarge && 'lg:h-20 lg:w-20')}
            fill='none'
            height='64'
            viewBox='0 0 64 64'
            width='64'
            xmlns='http://www.w3.org/2000/svg'
          >
            <circle cx='32' cy='32' fill='#0DA5A5' r='32' />
            <path
              d='M43.3333 42H21.6667C20.7467 42 20 41.2533 20 40.3333V23.6667C20 22.7467 20.7467 22 21.6667 22H43.3333C44.2533 22 45 22.7467 45 23.6667V40.3333C45 41.2533 44.2533 42 43.3333 42Z'
              stroke='white'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeMiterlimit='10'
              strokeWidth='2'
            />
            <path
              d='M23.3333 27L32.5 34.5L41.6667 27'
              stroke='white'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeMiterlimit='10'
              strokeWidth='2'
            />
            <path
              d='M23.3333 37L25.8333 34.5'
              stroke='white'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeMiterlimit='10'
              strokeWidth='2'
            />
            <path
              d='M41.6667 37L39.1667 34.5'
              stroke='white'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeMiterlimit='10'
              strokeWidth='2'
            />
          </svg>
        </EmailShareButton>
      </li>
      <li>
        <CopyToClipboard onCopy={() => setCopied(true)} text={shareUrl}>
          <button>
            <svg
              className={cn(
                'transition-transform',
                copied ? 'rotate-360' : 'rotate-0',
                isSmall && 'h-12 w-12',
                isLarge && 'lg:h-20 lg:w-20',
              )}
              fill='none'
              height='64'
              viewBox='0 0 64 64'
              width='64'
              xmlns='http://www.w3.org/2000/svg'
            >
              <circle cx='32' cy='32' fill='#ED255E' r='32' />
              <g className={cn('transition-opacity', copied ? ' opacity-0' : 'opacity-1')}>
                <path
                  d='M42.2969 23.0938L39.9062 20.7031C39.5312 20.3281 38.8281 20 38.3125 20H30.25C28.9844 20 28 21.0312 28 22.25V24.5H24.25C22.9844 24.5 22 25.5312 22 26.75V41.75C22 43.0156 22.9844 44 24.25 44H34.75C35.9688 44 37 43.0156 37 41.75V39.5H40.75C41.9688 39.5 43 38.5156 43 37.25V24.6875C43 24.1719 42.6719 23.4688 42.2969 23.0938ZM38.5 21.5469C38.5938 21.5938 38.7344 21.6406 38.8281 21.7344L41.2656 24.1719C41.3594 24.2656 41.4062 24.4062 41.4531 24.5H38.5V21.5469ZM35.5 41.75C35.5 42.1719 35.125 42.5 34.75 42.5H24.25C23.8281 42.5 23.5 42.1719 23.5 41.75V26.75C23.5 26.375 23.8281 26 24.25 26H28V37.25C28 38.5156 28.9844 39.5 30.25 39.5H35.5V41.75ZM41.5 37.25C41.5 37.6719 41.125 38 40.75 38H30.25C29.8281 38 29.5 37.6719 29.5 37.25V22.25C29.5 21.875 29.8281 21.5 30.25 21.5H37V24.875C37 25.5312 37.4688 26 38.125 26H41.5V37.25Z'
                  fill='white'
                />
              </g>
              <g className={cn('transition-opacity', copied ? ' opacity-0' : 'opacity-1')}>
                <path
                  d='M19.7959 42.8389C19.7406 43.6038 19.4574 44.2061 18.9463 44.6455C18.4385 45.085 17.7679 45.3047 16.9346 45.3047C16.0231 45.3047 15.3053 44.9987 14.7812 44.3867C14.2604 43.7715 14 42.9284 14 41.8574V41.4229C14 40.7393 14.1204 40.137 14.3613 39.6162C14.6022 39.0954 14.9456 38.6966 15.3916 38.4199C15.8408 38.14 16.3617 38 16.9541 38C17.7744 38 18.4352 38.2197 18.9365 38.6592C19.4378 39.0986 19.7275 39.7155 19.8057 40.5098H18.3408C18.305 40.0508 18.1764 39.7188 17.9551 39.5137C17.737 39.3053 17.4033 39.2012 16.9541 39.2012C16.4658 39.2012 16.0996 39.377 15.8555 39.7285C15.6146 40.0768 15.4909 40.6188 15.4844 41.3545V41.8916C15.4844 42.6598 15.5999 43.2214 15.8311 43.5762C16.0654 43.931 16.4333 44.1084 16.9346 44.1084C17.387 44.1084 17.724 44.0059 17.9453 43.8008C18.1699 43.5924 18.2985 43.2718 18.3311 42.8389H19.7959Z'
                  fill='white'
                />
                <path
                  d='M26.5977 41.8135C26.5977 42.5133 26.474 43.127 26.2266 43.6543C25.9792 44.1816 25.6243 44.5885 25.1621 44.875C24.7031 45.1615 24.1758 45.3047 23.5801 45.3047C22.9909 45.3047 22.4652 45.1631 22.0029 44.8799C21.5407 44.5967 21.1826 44.193 20.9287 43.6689C20.6748 43.1416 20.5462 42.5361 20.543 41.8525V41.501C20.543 40.8011 20.6683 40.1859 20.9189 39.6553C21.1729 39.1214 21.5293 38.7129 21.9883 38.4297C22.4505 38.1432 22.9779 38 23.5703 38C24.1628 38 24.6885 38.1432 25.1475 38.4297C25.6097 38.7129 25.9661 39.1214 26.2168 39.6553C26.4707 40.1859 26.5977 40.7995 26.5977 41.4961V41.8135ZM25.1133 41.4912C25.1133 40.7458 24.9798 40.1794 24.7129 39.792C24.446 39.4046 24.0651 39.2109 23.5703 39.2109C23.0788 39.2109 22.6995 39.403 22.4326 39.7871C22.1657 40.168 22.0306 40.7279 22.0273 41.4668V41.8135C22.0273 42.5394 22.1608 43.1025 22.4277 43.5029C22.6947 43.9033 23.0788 44.1035 23.5801 44.1035C24.0716 44.1035 24.4492 43.9115 24.7129 43.5273C24.9766 43.14 25.11 42.5768 25.1133 41.8379V41.4912Z'
                  fill='white'
                />
                <path
                  d='M29.127 42.7021V45.207H27.6621V38.0977H30.4355C30.9694 38.0977 31.4382 38.1953 31.8418 38.3906C32.2487 38.5859 32.5612 38.8643 32.7793 39.2256C32.9974 39.5837 33.1064 39.9922 33.1064 40.4512C33.1064 41.1478 32.8672 41.6979 32.3887 42.1016C31.9134 42.502 31.2542 42.7021 30.4111 42.7021H29.127ZM29.127 41.5156H30.4355C30.8229 41.5156 31.1175 41.4245 31.3193 41.2422C31.5244 41.0599 31.627 40.7995 31.627 40.4609C31.627 40.1126 31.5244 39.8311 31.3193 39.6162C31.1143 39.4014 30.8311 39.2907 30.4697 39.2842H29.127V41.5156Z'
                  fill='white'
                />
                <path d='M35.6748 45.207H34.21V38.0977H35.6748V45.207Z' fill='white' />
                <path
                  d='M41.3145 42.126H38.502V44.0303H41.8027V45.207H37.0371V38.0977H41.793V39.2842H38.502V40.9785H41.3145V42.126Z'
                  fill='white'
                />
                <path
                  d='M42.6621 45.207V38.0977H44.8496C45.4746 38.0977 46.0329 38.2393 46.5244 38.5225C47.0192 38.8024 47.4049 39.2028 47.6816 39.7236C47.9583 40.2412 48.0967 40.8304 48.0967 41.4912V41.8184C48.0967 42.4792 47.96 43.0667 47.6865 43.5811C47.4163 44.0954 47.0339 44.4941 46.5391 44.7773C46.0443 45.0605 45.486 45.2038 44.8643 45.207H42.6621ZM44.127 39.2842V44.0303H44.835C45.4079 44.0303 45.8457 43.8431 46.1484 43.4688C46.4512 43.0944 46.6058 42.5589 46.6123 41.8623V41.4863C46.6123 40.7637 46.4626 40.2168 46.1631 39.8457C45.8636 39.4714 45.4258 39.2842 44.8496 39.2842H44.127Z'
                  fill='white'
                />
                <path
                  d='M50.5088 43.0244H49.3369L49.1709 38.0977H50.6748L50.5088 43.0244ZM49.9229 43.7959C50.1605 43.7959 50.3509 43.8659 50.4941 44.0059C50.6406 44.1458 50.7139 44.3249 50.7139 44.543C50.7139 44.7578 50.6406 44.9352 50.4941 45.0752C50.3509 45.2152 50.1605 45.2852 49.9229 45.2852C49.6885 45.2852 49.498 45.2152 49.3516 45.0752C49.2083 44.9352 49.1367 44.7578 49.1367 44.543C49.1367 44.3281 49.2083 44.1507 49.3516 44.0107C49.498 43.8675 49.6885 43.7959 49.9229 43.7959Z'
                  fill='white'
                />
                <path
                  d='M40.418 19L29.418 30L24.418 25'
                  stroke='white'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                />
              </g>
            </svg>
          </button>
        </CopyToClipboard>
      </li>
    </ul>
  );
}
